import { useMemo } from 'react';
import { useCustom } from '@refinedev/core';
import { Bot, Users, Ban, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormHeader } from '@/components/forms';
import { DataTable, type Column } from '@/components/tables';
import { formatNumber, formatPercent } from '@/lib/translations';

interface TelegramBotStatRow {
  botId: number;
  botName: string;
  botUsername: string | null;
  total: number;
  active: number;
  blocked: number;
}

const getBanRate = (blocked: number, total: number): number => {
  if (total <= 0) {
    return 0;
  }
  return (blocked / total) * 100;
};

const getBanRateBadge = (rate: number) => {
  if (rate >= 30) {
    return (
      <Badge variant="destructive" className="text-xs">
        {formatPercent(rate)}
      </Badge>
    );
  }
  if (rate >= 15) {
    return (
      <Badge className="bg-orange-500/10 text-orange-600 text-xs">
        {formatPercent(rate)}
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="text-xs">
      {formatPercent(rate)}
    </Badge>
  );
};

export function TelegramBotsStatsList() {
  const statsQuery = useCustom<TelegramBotStatRow[]>({
    url: '/telegram-bots/stats',
    method: 'get',
  });

  const isLoading = statsQuery.query?.isLoading ?? false;
  const stats: TelegramBotStatRow[] = useMemo(
    () => statsQuery.query?.data?.data ?? [],
    [statsQuery.query?.data?.data],
  );

  const totalLinks = useMemo(() => stats.reduce((sum, row) => sum + row.total, 0), [stats]);
  const totalBlocked = useMemo(() => stats.reduce((sum, row) => sum + row.blocked, 0), [stats]);

  const columns: Column<TelegramBotStatRow & { id: number }>[] = [
    {
      key: 'botName',
      header: 'Бот',
      render: (_, record) => (
        <div className="flex items-center gap-2">
          <Bot className="h-4 w-4 text-muted-foreground" />
          <div className="flex flex-col">
            <span className="font-medium text-sm">{record.botName}</span>
            {record.botUsername && (
              <a
                href={`https://t.me/${record.botUsername}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-500 hover:underline"
              >
                @{record.botUsername}
              </a>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'total',
      header: 'Всего',
      render: (_, record) => (
        <span className="font-medium tabular-nums">{formatNumber(record.total)}</span>
      ),
    },
    {
      key: 'active',
      header: 'Активных',
      render: (_, record) => (
        <span className="text-green-600 tabular-nums">{formatNumber(record.active)}</span>
      ),
    },
    {
      key: 'blocked',
      header: 'Забанили',
      render: (_, record) => (
        <span className="text-destructive tabular-nums">{formatNumber(record.blocked)}</span>
      ),
    },
    {
      key: 'banRate',
      header: '% бана',
      render: (_, record) => getBanRateBadge(getBanRate(record.blocked, record.total)),
    },
  ];

  const data = stats.map((row) => ({ ...row, id: row.botId }));

  return (
    <div className="space-y-6">
      <FormHeader
        title="Telegram боты"
        description="Сводка по взаимодействию пользователей с ботами"
        icon={<Bot className="h-5 w-5" />}
        sticky
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Users className="h-4 w-4" />
              Всего связей
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold tabular-nums">{formatNumber(totalLinks)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Ban className="h-4 w-4 text-destructive" />
              Забанили
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold tabular-nums text-destructive">
              {formatNumber(totalBlocked)}
            </div>
          </CardContent>
        </Card>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={data}
          total={data.length}
          page={1}
          pageSize={100}
          onPageChange={() => {}}
          emptyMessage="Нет ботов"
          rowKey="id"
        />
      )}
    </div>
  );
}
